import { Account, AssetClass, TradingSystem } from '@monorepo/types';
import { TRADING_INSTRUMENTS } from '../constants/trading-instruments.constants';
import type { TradingInstrumentConfig } from '../types/tag-data.types';

/**
 * Mapping from TradingSystem enum to AssetClass
 * Used as fallback when account.assetClass field is not available
 */
const TRADING_SYSTEM_TO_ASSET_CLASS_MAP: Record<TradingSystem, AssetClass> = {
  [TradingSystem.MatchTrader]: AssetClass.Forex,
  [TradingSystem.TraderEvolution]: AssetClass.Stocks,
  [TradingSystem.MetaTraderFive]: AssetClass.Forex,
  [TradingSystem.MetaTraderXP]: AssetClass.Forex,
  [TradingSystem.CTrader]: AssetClass.Forex,
  [TradingSystem.MetaTraderContest]: AssetClass.Forex,
  [TradingSystem.BlackArrowFutures]: AssetClass.Futures,
};

/**
 * Mapping from AssetClass to TradingInstrumentConfig
 */
const ASSET_CLASS_TO_TRADING_INSTRUMENT_MAP: Record<
  AssetClass,
  TradingInstrumentConfig
> = {
  [AssetClass.Forex]: TRADING_INSTRUMENTS.FOREX,
  [AssetClass.Futures]: TRADING_INSTRUMENTS.FUTURES,
  [AssetClass.Stocks]: TRADING_INSTRUMENTS.STOCKS,
};

/**
 * Determines asset class from Account
 * Priority:
 * 1. account.assetClass field (if present)
 * 2. tradingSystem mapping (fallback)
 *
 * @param account - Account object or null
 * @returns AssetClass enum value or null if cannot determine
 */
export const getAssetTypeFromAccount = (
  account: Account | null,
): AssetClass | null => {
  if (!account) {
    return null;
  }

  // Check account.assetClass field first (if present)
  if (account.assetClass) {
    return account.assetClass;
  }

  // Fallback to tradingSystem mapping
  if (!account.tradingSystem) {
    return null;
  }

  return (
    TRADING_SYSTEM_TO_ASSET_CLASS_MAP[account.tradingSystem] ?? AssetClass.Forex // Default fallback to Forex for backward compatibility
  );
};

/**
 * Gets asset class override from query parameters for QA testing
 *
 * @param searchParams - URL search parameters
 * @returns AssetClass enum value or null if no override present
 */
export const getAssetTypeOverride = (
  searchParams: URLSearchParams,
): AssetClass | null => {
  const assetTypeParam = searchParams.get('assetType');
  if (assetTypeParam) {
    const normalized = assetTypeParam.toLowerCase();
    if (normalized === 'forex') return AssetClass.Forex;
    if (normalized === 'futures') return AssetClass.Futures;
    if (normalized === 'stocks') return AssetClass.Stocks;
  }

  return null;
};

/**
 * Maps AssetClass enum to TradingInstrumentConfig
 *
 * @param assetClass - AssetClass enum value or null
 * @returns TradingInstrumentConfig or default FOREX if null
 */
export const mapAssetTypeToTradingInstrument = (
  assetClass: AssetClass | null,
): TradingInstrumentConfig => {
  return (
    ASSET_CLASS_TO_TRADING_INSTRUMENT_MAP[assetClass ?? AssetClass.Forex] ??
    TRADING_INSTRUMENTS.FOREX
  );
};
